import React from 'react';
import { Box, Flex, Icon } from '@qga/roo-ui/components';
import PropTypes from 'prop-types';
import { path } from 'ramda';

import ToggleContent from '@shared/v2/components/ToggleContent';

import LinkButton from '@/components/LinkButton';
import PillContainer from '@/components/PillContainer';
import PromoPill from '@/components/PromoPill';
import Skeleton from '@/components/Skeleton';
import Text from '@/components/Text';
import useCampaignPills from '@/hooks/useCampaignPills';
import { isSameDay } from '@/lib/date';
import Flight from '@/propTypes/Flight';
import formatStops from '@/utils/formatStops';
import mapDurationFromFlights from '@/utils/mapDurationFromFlights';
import { useGA4Events } from '@/v2/hooks/featureFlags/ga4Events/useGA4Events';
import { dataLayer } from '@/v2/utils/dataLayer';
import TerminalTime from '../TerminalTime';
import FlightDetails from './components/FlightDetails';
import OvernightBadge from './components/OvernightBadge';

const Segment = ({ flights, loading = false, cta }) => {
  const firstFlight = flights && flights[0];
  const lastFlight = flights && flights[flights.length - 1];
  const campaignCode = firstFlight && firstFlight.promotionCode;
  const shouldUseNewEvents = useGA4Events();

  const nextDay =
    firstFlight &&
    !isSameDay(
      new Date(firstFlight.departure.date),
      new Date(lastFlight.arrival.date),
    );
  const { campaignPills } = useCampaignPills();

  const renderStopDurations = () => (
    <Box>
      <Text medium>
        <Skeleton visible={loading} width="50px">
          {() => formatStops(flights.length)}
        </Skeleton>
      </Text>
      <Text medium mx="2">
        –
      </Text>
      <Text medium>
        <Skeleton visible={loading} width="100px">
          {() => {
            const duration = mapDurationFromFlights(flights);
            return (
              <>
                <Text
                  medium
                >{`${duration.hour} hr ${duration.minute} min`}</Text>
                {nextDay && <OvernightBadge />}
              </>
            );
          }}
        </Skeleton>
      </Text>
    </Box>
  );

  const renderFlightsJourney = (isShown, onToggled) => (
    <Flex
      flexDirection={['column', null, 'row']}
      alignItems={[null, null, 'center']}
    >
      <Flex alignItems="center">
        <Box>
          <TerminalTime
            loading={loading}
            segmentTime={firstFlight && firstFlight.departure.time}
            direction="Departure"
            locationCode={firstFlight && firstFlight.origin.code}
          />
        </Box>
        <Flex mx={[5, null, 10]} width="56px" justifyContent={'center'}>
          {flights &&
            flights.map((_, index) => (
              <Icon
                name="flightRight"
                size={20}
                color="icon.inclusion"
                key={index}
                mx={1}
              />
            ))}
        </Flex>
        <Box>
          <TerminalTime
            loading={loading}
            isNextDay={nextDay}
            segmentTime={lastFlight && lastFlight.arrival.time}
            direction="Arrival"
            locationCode={lastFlight && lastFlight.destination.code}
          />
        </Box>
      </Flex>
      <Box ml={[null, null, 10]} mt={[2, null, 0]}>
        <Flex flexDirection="column">
          <Box>{renderStopDurations()}</Box>
          <Box mt={[2, null, 2]}>
            <Skeleton visible={loading} width="120px">
              {() => (
                <LinkButton
                  data-testid="TOGGLE_FLIGHT_DETAILS"
                  noPadding
                  onClick={onToggled}
                >
                  <Flex alignItems="center">
                    <Text bold link medium>
                      {isShown ? 'Hide details' : 'Show details'}
                    </Text>
                    <Icon
                      name={isShown ? 'keyboardArrowUp' : 'keyboardArrowDown'}
                      size={20}
                    />
                  </Flex>
                </LinkButton>
              )}
            </Skeleton>
          </Box>
        </Flex>
      </Box>
    </Flex>
  );

  return (
    <Flex
      flexDirection={['column', null, 'row']}
      justifyContent={[null, null, 'space-between']}
      alignItems={[null, null, 'center']}
      mx={['4', null, '10']}
      flexWrap={[null, null, 'wrap']}
    >
      {campaignCode && (
        <PillContainer leftOffset={-2}>
          <PromoPill
            message={path([campaignCode], campaignPills)?.flightPillText}
            background={'ui.badgeBackground'}
            textColor="text.heading"
          />
        </PillContainer>
      )}
      <ToggleContent
        toggle={(open, { isShown, hide }) => {
          const onToggled = () => {
            if (!isShown && shouldUseNewEvents && !!flights) {
              const itemName =
                flights?.length < 2
                  ? `${flights[0].flightNumber} ${flights[0].flightClass}`
                  : `${flights[0].flightNumber} ${flights[0].flightClass} - ${flights[1].flightNumber} ${flights[1].flightClass}`;

              dataLayer.accordionOpenEvent({
                groupName: 'Flights Page',
                itemName,
              });
            }

            return isShown ? hide() : open();
          };

          return (
            <Flex
              justifyContent="space-between"
              flexDirection={['column', null, 'row']}
              pt={[6, null, 8]}
              pb={[0, null, 8]}
            >
              {renderFlightsJourney(isShown, onToggled)}
            </Flex>
          );
        }}
        content={() => <FlightDetails flights={flights} />}
      />
      {cta && (
        <Flex
          data-testid="FLIGHT-SEGMENT-CTA"
          justifyContent="center"
          alignItems="center"
          mb={['6', null, '0']}
        >
          {cta}
        </Flex>
      )}
    </Flex>
  );
};

Segment.propTypes = {
  flights: PropTypes.arrayOf(PropTypes.shape(Flight)),
  loading: PropTypes.bool,
  cta: PropTypes.node,
};

export default Segment;
